<template>
  <div class="flex flex-col w-full relative">
    <div class="flex !grow-0 items-center gap-4 pa-2" style="width: 100%; font-weight: bold; overflow: auto">
      <div class="cursor-pointer flex items-center gap-2" @click="showFormationTab()">
        <v-icon>mdi-vector-circle</v-icon>
        <span :class="{ selectedTab: isFormationTabVisible }">{{ $t('Fleet.FormationTitle') }}</span>
      </div>
      |
      <div class="cursor-pointer flex items-center gap-2" @click="showMissionPlanTab()">
        <v-icon>mdi-group</v-icon>
        <span :class="{ selectedTab: isMissionPlanTabVisible }">{{ $t('Fleet.MainMissionPlan') }}</span>
      </div>
      |
      <div class="cursor-pointer flex items-center gap-2" @click="showAssociationTab()">
        <v-icon>mdi-swap-horizontal</v-icon>
        <span :class="{ selectedTab: isAssociationTabVisible }">{{ $t('Fleet.GliderAssociation') }}</span>
      </div>
      <v-spacer />
      <div class="flex items-center gap-1">
        <v-btn class="ma-0" icon small @click="bestFit()"><v-icon>mdi-target</v-icon></v-btn>
      </div>
    </div>
    <div class="grow w-full" style="position: relative; background-color: white">
      <div class="scrollable-container" :class="{ 'mx-4': isAssociationTabVisible, 'mx-2': !isAssociationTabVisible }">
        <FleetFormationParameter v-if="isVisible && isFormationTabVisible" />
        <FleetFormationMissionPlan v-if="isVisible && isMissionPlanTabVisible" />
        <FleetGliderAssociation ref="gliderAssociation" v-if="isVisible && isAssociationTabVisible" />
      </div>
    </div>
    <div class="flex shrink-0 !grow-0 w-full mt-auto items-center gap-2">
      <v-autocomplete
        v-if="!original.param.start_date"
        :items="covIterationsTemplates"
        item-text="COV_LIBELLE"
        item-value="COV_ID"
        :label="$t('Fleet.LoadFromTemplate')"
        v-model="templateId"
        class="ml-1"
        :append-outer-icon="templateId ? 'mdi-reload' : ''"
        @click:append-outer="loadFromTemplate()"
      ></v-autocomplete>
      <v-btn :disabled="!btnActif" @click="restoreConfig()" small class="ml-auto !grow-0">
        {{ $t('Pilot.MissionPlan.RestoreConfig') }}
        <v-icon small right>mdi-replay</v-icon>
      </v-btn>
      <v-btn :disabled="!btnActif" @click="saveConfig()" small color="primary" class="!grow-0">
        {{ $t('Pilot.MissionPlan.PublishStagingToIris') }}
        <v-icon small right>mdi-content-save</v-icon>
      </v-btn>
      <v-btn v-if="!original.param.start_date" @click="startFleet()" small color="primary" class="!grow-0">
        {{ $t('Fleet.StartFleet') }}
        <v-icon small right>mdi-play-outline</v-icon>
      </v-btn>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick, getCurrentInstance } from 'vue'
import { MissionPlanUiState, buildMissionPlanStateId } from '@/models/ui/MissionPlanUiState'
import FleetGliderAssociation from '@/components/fleet/FleetGliderAssociation.vue'
import deepEqual from 'deep-equal'
import { buildSimulationUIStateId } from '@/models/ui/SimulationUiState'
import { buildCoverageStateId } from '@/models/ui/CoverageUiState'
import FleetFormationParameter from '@/components/fleet/FleetFormationParameter.vue'
import FleetFormationMissionPlan from '@/components/fleet/FleetFormationMissionPlan.vue'
import store from '@/store'
import { AloDbFleet, AloDbFleetFormation } from '@/models/alodb/AloDbFleet'
import { buildFleetUIStateId } from '@/models/ui/FleetUiState'
import { AloDbMissionGlobalFormation, isAloDbMissionGlobalFormation } from '@/models/alodb/AloDbMissionGlobal'
import { CoverageStep } from '@/enum/CoverageStep'
import { CoverageState } from '@/enum/CoverageState'
import { AloDbCoverageIteration } from '@/models/alodb/AloDbCoverageIteration'
import { ISmartSimulationFormation } from '@/models/iris/smartMission/ISmartSimulationFormation'
import { cloneDeep } from 'lodash'
import { isReadyForFleetFormation } from '@/models/iris/fleet/IFleetParamFormation'
import { isReadyForIrisFleetFormation } from '@/models/iris/fleet/IFleetIrisConfigFormation'
import { i18n } from '@/locale/vueI18n'
import { VuetifyConfirmObject } from 'vuetify-confirm/types'
import { getDefaultGliderCoverageConfig, IGliderCoverageConfig } from '@/models/iris/smartMission/IGliderCoverageConfig'
import { getDefaultGliderConfig } from '@/models/iris/smartMission/IGliderConfig'
import { extractDistinctProp } from '@/utils/groupBy'
import {
  getDefaultGliderFleetFormationConfig,
  IGliderFleetFormationConfig,
} from '@/models/iris/fleet/IGliderFleetFormationConfig'

const props = defineProps<{
  isleft: boolean
  uiid: string
  isVisible: boolean
}>()

// Accès aux plugins Vue via l'instance (Vue 2 + Composition API)
const instance = getCurrentInstance()
const $confirm = instance?.proxy?.$confirm!

const templateId = ref(null)

const gliderAssociation = ref(null)

store.myActions.MISSIONVIRTUAL_ENSURE_LIST()
store.myActions.COVERAGEITERATION_ENSURE_LIST()
store.myActions.MISSIONGLOBAL_ENSURE_LIST()

const visibleTab = ref('formation') // configuration || formation || missionplan

const isMissionPlanTabVisible = computed(() => visibleTab.value === 'missionplan')
const isFormationTabVisible = computed(() => visibleTab.value === 'formation')
const isAssociationTabVisible = computed(() => visibleTab.value === 'association')

const modified = computed(() => store.myGetters.modifiedAdmAdminFleet as AloDbFleetFormation)
const original = computed(() => store.myGetters.originalAdmAdminFleet as AloDbFleetFormation)

const map_uiid = computed(() => buildFleetUIStateId(original.value.FLE_ID!, 'map'))

const btnActif = computed(() => !deepEqual(original.value, modified.value) /*&& !editCoverageState.value!.saving*/)

const missionPlanUiid = computed(() => buildMissionPlanStateId(modified.value.FLE_ID || -1, 'fleet'))
const missionPlanUiState = computed(() => store.myGetters.missionPlanUiStateByUiid(missionPlanUiid.value))

const plan = computed({
  get: () => missionPlanUiState.value.missionPlan,
  set: value => {
    store.myActions.UI_MISSIONPLAN_SET_MISSIONPLAN({
      uiid: missionPlanUiid.value,
      plan: value,
    })
  },
})

const mission_plan = computed(() => modified.value.config.barycentre_mission_plan)
const selectedFilterEnsIds = computed(() => {
  //@ts-ignore
  return store.myGetters.filterMission?.ensIds?.at(0) ?? undefined
})

const missionGlobals = computed(() => {
  return store.myGetters.missionGlobals.filter(
    m =>
      isAloDbMissionGlobalFormation(m) &&
      //@ts-ignore
      ((!store.myGetters.isOperator && !store.myGetters.isSysAdmin) ||
        selectedFilterEnsIds.value == undefined ||
        m.ENS_ID == selectedFilterEnsIds.value),
  ) as Array<AloDbMissionGlobalFormation>
})

const endedCoverageIterations = computed(() => {
  return store.myGetters.coverageIterations.filter(
    ci =>
      ci.COV_PROCESSING_STEP == CoverageStep.FinalSimulation &&
      (ci.COV_PROCESSING_STATE == CoverageState.aborted || ci.COV_PROCESSING_STATE == CoverageState.finished),
  )
})

const maxEndedCoverageIterationIdByMigId = computed(() => {
  return endedCoverageIterations.value.reduce((acc, it) => {
    if (!acc[it.MIG_ID!] || acc[it.MIG_ID!] < it.COV_ID!) acc[it.MIG_ID!] = it.COV_ID!
    return acc
  }, {} as { [key: number]: number })
})

const covIterationsTemplates = computed(() => {
  return missionGlobals.value
    .reduce((list, m) => {
      const maxCovId = maxEndedCoverageIterationIdByMigId.value[m.MIG_ID!]
      const covIteration =
        store.myGetters.coverageIterationsById[(m.config?.templateSimId ?? 0) > 0 ? m.config!.templateSimId! : maxCovId]
      if (covIteration?.resultMipJson) {
        list.push(covIteration)
      }
      return list
    }, [] as Array<AloDbCoverageIteration>)
    .filter(c => (c.config?.mission_plan?.steps?.length ?? 0) > 0)
})

function loadFromTemplate() {
  if (templateId.value) {
    const cov = store.myGetters.coverageIterationsById[templateId.value]
    const conf = cov.config as any as ISmartSimulationFormation

    modified.value.config.glider_number = conf.gliders_config.length
    AloDbFleet.correctGliderAssociation(modified.value)

    modified.value.param.distance = conf.distance
    modified.value.param.ratio_ellipse = conf.ratio_ellipse
    modified.value.param.rotation = conf.rotation

    //console.log("mission_plan",conf.mission_plan)

    modified.value.config.barycentre_mission_plan = cloneDeep(conf.mission_plan)

    templateId.value = null
  }
}

function showMissionPlanTab() {
  visibleTab.value = 'missionplan'
}

function showFormationTab() {
  visibleTab.value = 'formation'
}

function showAssociationTab() {
  visibleTab.value = 'association'
}

function bestFit() {
  store.myActions.UI_REFRESH_BEST_FIT_MAP(map_uiid.value)
}

async function restoreConfig() {
  await store.myActions.ADMINFLEET_CANCEL_MODIFIED_ADM()
  store.myActions.UI_MISSIONPLAN_SET_MISSIONPLAN({
    uiid: missionPlanUiid.value,
    plan: modified.value.config?.barycentre_mission_plan,
  })
  if (gliderAssociation.value) {
    //@ts-ignore
    gliderAssociation.value.genereAssociation()
  }
}

async function saveConfig() {
  //la flotte est lancé
  if (original.value.param.start_date) {
    const errors = isReadyForIrisFleetFormation(modified.value.config)
    if (!errors?.length) {
      generateGliderConfig()
      await store.myActions.ADMINFLEET_SAVE_REQUEST()
      await store.myActions.ADMINFLEET_PUBLISH_IRIS_REQUEST(modified.value.config)
    } else {
      $confirm(`<ul><li>${errors.map(e => i18n.t(`Fleet.Check.${e}`)).join('</li><li>')}</li></ul>`, {
        title: i18n.t('Simulation.Check.Title'),
        buttonTrueText: 'Ok',
        buttonFalseText: '',
      } as VuetifyConfirmObject)
    }
  } else {
    await store.myActions.ADMINFLEET_SAVE_REQUEST()
  }
}

function generateGliderConfig() {
  modified.value.config.gliders_config = Object.keys(modified.value.config.glider_association).reduce(
    (gliConf, key) => {
      const association = modified.value.config.glider_association[key]
      const gliKey = association.at(0) as string
      const misIdx = association.at(1)
      const mission = store.myGetters.missions.find(m => m.gliKey?.trim() == gliKey.trim() && m.misIdx == misIdx)

      if (mission) {
        const capteur = extractDistinctProp(mission!.mesureTypeExArray, m => m?.UMT_KEY ?? '').map((p, idx) => ({
          id: idx + 1,
          snr_label: p,
        }))
        const lastYo = mission.yoArray?.at(-1)
        gliConf[gliKey] = getDefaultGliderFleetFormationConfig({
          libelle: gliKey,
          payload: capteur ?? undefined,
          start_energy: lastYo?.batteries ?? undefined,
          prf_sci: [
            {
              id: 1,
              prf_label: 'default',
              sensors:
                capteur?.reduce((s, p) => {
                  s[p.id] = [1, '111', 1]
                  return s
                }, {} as { [key: string]: [number, string, number] }) ?? undefined,
            },
          ],
        })
      }
      return gliConf
    },
    {} as Record<string, IGliderFleetFormationConfig>,
  )

  //supprime les prf_sci du mission plan
  modified.value.config.barycentre_mission_plan.prf_sci = []
  modified.value.config.barycentre_mission_plan.steps.forEach(s => {
    //@ts-ignore
    s.prfSci_id = null
  })
}

async function startFleet() {
  const errors = isReadyForIrisFleetFormation(modified.value.config)
  if (!errors?.length) {
    modified.value.param.start_date = new Date().toISOString()
    modified.value.config.fleet_name = modified.value.FLE_NAME!
    modified.value.config.fleet_company = modified.value.FLE_COMPANY!

    generateGliderConfig()

    await saveConfig()

    await store.myActions.ADMINFLEET_PUBLISH_IRIS_REQUEST(modified.value.config)
  } else {
    $confirm(`<ul><li>${errors.map(e => i18n.t(`Fleet.Check.${e}`)).join('</li><li>')}</li></ul>`, {
      title: i18n.t('Simulation.Check.Title'),
      buttonTrueText: 'Ok',
      buttonFalseText: '',
    } as VuetifyConfirmObject)
    await saveConfig()
  }
}

// Watch for changes in original to update plan
watch(original, () => {
  if (!btnActif.value) {
    plan.value = mission_plan.value
  }
})
</script>
